import type { PreparedAsset } from "@brains/assets";
import type {
  ActorRef,
  EntityReadBudget,
  QueryEmbedding,
} from "@brains/contracts";
import type { ProjectionStore } from "./projection-store";
import type { ProjectionChangedTarget } from "./schema/projection-state";
import type {
  AcknowledgeEntityExportsRequest,
  EntityExportIntent,
} from "./entity-export-types";
export type {
  AcknowledgeEntityExportsRequest,
  EntityExportAcknowledgement,
  EntityExportIntent,
} from "./entity-export-types";
import { z } from "@brains/utils/zod";
import {
  contentVisibilitySchema,
  type ContentVisibility,
  type RawContentVisibility,
} from "./visibility";

/**
 * Entity type for unstructured notes (the "note" entity type).
 * Used as a sentinel for the default catch-all markdown file shape —
 * no typed frontmatter schema required, content is the entire file body.
 */
export const NOTE_ENTITY_TYPE = "note";

/**
 * Embedding job data - minimal data for job queue
 * Content is NOT stored to avoid large base64 data in job queue
 * (which would end up in dashboard hydration props JSON)
 * Handler fetches fresh content from entity when processing
 */
export interface EmbeddingJobData {
  id: string;
  entityType: string;
  /** Hash of content at job creation time - for staleness detection */
  contentHash: string;
  operation: "create" | "update";
}

/**
 * Options for entity mutation operations (create, update, upsert)
 */
export interface EntityMutationEventContext {
  conversationId?: string;
  channelId?: string;
  runId?: string;
  toolCallId?: string;
  actor?: ActorRef;
  interfaceType?: string;
}

export type {
  EntityMutationAdmission,
  EntityMutationAdmissionTarget,
} from "./mutation-admission";

import type { EntityWriteCondition } from "./entity-write-contracts";

export interface EntityWriteSnapshot {
  entity: BaseEntity;
  revision: string;
}

export type EntityPersistenceOrigin = "ordinary" | "directory-sync";

export interface EntityJobOptions {
  priority?: number;
  maxRetries?: number;
  eventContext?: EntityMutationEventContext;
  /**
   * Identifies authoritative file imports/deletes so they clear, rather than
   * create, an outbound directory-export intent.
   */
  persistenceOrigin?: EntityPersistenceOrigin;
}

export {
  canonicalContentVisibilitySchema,
  contentVisibilitySchema,
  normalizeContentVisibility,
  getVisibleContentVisibilities,
  isVisibleWithinScope,
  permissionToVisibilityScope,
  canWriteVisibility,
} from "./visibility";
export type { ContentVisibility, RawContentVisibility } from "./visibility";

/**
 * Options for entity creation (extends EntityJobOptions with deduplication)
 */
export interface CreateEntityOptions extends EntityJobOptions {
  /** Cancel before the atomic write boundary, not after a mutation commits. */
  signal?: AbortSignal;
  /** Runtime-only guard over the final persisted fields, immediately before writing. */
  beforeWrite?: (entity: Readonly<BaseEntity>) => Promise<void>;
  deduplicateId?: boolean;
  /** Atomic create-if-absent. Cannot deduplicate. */
  conditionalWrite?: EntityWriteCondition;
}

/** Options for updating an existing entity. */
export interface UpdateEntityOptions extends EntityJobOptions {
  /** Cancel before the atomic write boundary, not after a mutation commits. */
  signal?: AbortSignal;
  /** Runtime-only guard over the final persisted fields, immediately before writing. */
  beforeWrite?: (entity: Readonly<BaseEntity>) => Promise<void>;
  /** Apply only while the stored entity still has this content hash. */
  expectedContentHash?: string | undefined;
  /** Atomic full-revision replace. */
  conditionalWrite?: EntityWriteCondition;
}

/**
 * Result of an entity mutation that triggers an embedding job.
 * When skipped is true, no DB write, event, or embedding job was produced.
 */
export interface EntityMutationResult {
  entityId: string;
  jobId: string;
  skipped: boolean;
  skipReason?: "content-conflict" | undefined;
}

/**
 * Input for adapter-validated direct creation from finalized markdown.
 */
export interface CreateEntityFromMarkdownInput {
  entityType: string;
  id: string;
  markdown: string;
  /** Explicit core visibility. When omitted, markdown frontmatter or the public default applies. */
  visibility?: ContentVisibility;
}

/**
 * Data for storing an embedding for an entity
 */
export interface StoreEmbeddingData {
  entityId: string;
  entityType: string;
  embedding: Float32Array;
  contentHash: string;
}

/**
 * Base entity schema that all entities must extend
 */
export const baseEntitySchema: z.ZodObject<{
  id: z.ZodString;
  entityType: z.ZodString;
  content: z.ZodString;
  created: z.ZodString;
  updated: z.ZodString;
  visibility: typeof contentVisibilitySchema;
  metadata: z.ZodRecord<z.ZodString, z.ZodUnknown>;
  contentHash: z.ZodString;
}> = z.object({
  id: z.string(),
  entityType: z.string(),
  content: z.string(),
  created: z.string().datetime(),
  updated: z.string().datetime(),
  visibility: contentVisibilitySchema,
  metadata: z.record(z.string(), z.unknown()),
  contentHash: z.string(),
});

const canonicalContentVisibilityParserSchema: z.ZodEnum<{
  public: "public";
  shared: "shared";
  restricted: "restricted";
}> = z.enum(["public", "shared", "restricted"]);

/** Zod 4-owned parser base for entity schemas whose registration slot is structural. */
export const baseEntityParserSchema: z.ZodObject<{
  id: z.ZodString;
  entityType: z.ZodString;
  content: z.ZodString;
  created: z.ZodString;
  updated: z.ZodString;
  visibility: z.ZodPipe<
    z.ZodOptional<
      z.ZodUnion<
        readonly [
          typeof canonicalContentVisibilityParserSchema,
          z.ZodLiteral<"private">,
        ]
      >
    >,
    z.ZodTransform<ContentVisibility, RawContentVisibility | undefined>
  >;
  metadata: z.ZodRecord<z.ZodString, z.ZodUnknown>;
  contentHash: z.ZodString;
}> = z.object({
  id: z.string(),
  entityType: z.string(),
  content: z.string(),
  created: z.string().datetime(),
  updated: z.string().datetime(),
  visibility: z
    .union([canonicalContentVisibilityParserSchema, z.literal("private")])
    .optional()
    .transform((value): ContentVisibility => {
      if (value === undefined) return "public";
      if (value === "private") return "restricted";
      return value;
    }),
  metadata: z.record(z.string(), z.unknown()),
  contentHash: z.string(),
});

/** Shared empty frontmatter schema for entity types with no typed frontmatter. */
export const emptyFrontmatterSchema: z.ZodObject<Record<string, never>> =
  z.object({});

export type EntitySchema<T> = z.ZodType<T, unknown>;
/** @deprecated Use EntitySchema<T>. */
export type EntitySchemaParser<T> = EntitySchema<T>;

export type UnknownEntitySchema = EntitySchema<unknown>;
export type FrontmatterSchema = z.ZodObject<z.ZodRawShape>;

/**
 * Base entity type - generic to support typed metadata
 * TMetadata defaults to Record<string, unknown> for backward compatibility
 */
export interface BaseEntity<TMetadata = Record<string, unknown>> {
  id: string;
  entityType: string;
  content: string;
  created: string;
  updated: string;
  visibility: ContentVisibility;
  metadata: TMetadata;
  /** SHA256 hash of content for change detection */
  contentHash: string;
}

/**
 * Entity input type for creation - allows partial entities with optional system fields
 * contentHash is excluded because it's computed automatically by the entity service
 */
export type EntityInput<T extends BaseEntity> = Omit<
  T,
  "id" | "created" | "updated" | "contentHash" | "visibility"
> & {
  id?: string;
  created?: string;
  updated?: string;
  visibility?: RawContentVisibility;
};

/**
 * Search result type
 */
export interface SearchResult<T extends BaseEntity = BaseEntity> {
  entity: T;
  score: number;
  excerpt: string;
}

/**
 * Normalized system_create input shape used by plugin create interceptors.
 */
export interface CreateCoverImageInput {
  generate?: boolean | undefined;
  prompt?: string | undefined;
}

export interface CreateFromAttachmentInput {
  kind: "entity-attachment";
  sourceEntityType: string;
  sourceEntityId: string;
  attachmentType: string;
}

export interface CreateFromUploadInput {
  kind: "upload";
  id: string;
}

export interface CreateFromConversationMessageInput {
  kind: "conversation-message";
  messageId?: string | undefined;
}

export type CreateFromInput =
  | CreateFromAttachmentInput
  | CreateFromUploadInput
  | CreateFromConversationMessageInput;

export type CreateTransform = "extract-markdown" | "preserve";

export interface CreateInput {
  entityType: string;
  prompt?: string;
  title?: string;
  visibility?: ContentVisibility;
  content?: string;
  url?: string;
  from?: CreateFromInput;
  transform?: CreateTransform;
  replace?: boolean;
  sourceEntityType?: string;
  sourceEntityId?: string;
  sourceEntityIds?: string[];
  targetEntityType?: string;
  targetEntityId?: string;
  coverImage?: boolean | CreateCoverImageInput;
}

/**
 * Minimal caller context forwarded to plugin create interceptors.
 */
export interface CreateExecutionContext {
  interfaceType: string;
  actor: ActorRef;
  channelId?: string;
  channelName?: string;
}

/**
 * Result returned to system_create when a plugin fully handles creation.
 */
export const createResultAttachmentSchema: z.ZodObject<{
  mediaType: z.ZodString;
  url: z.ZodString;
  downloadUrl: z.ZodOptional<z.ZodString>;
  previewUrl: z.ZodOptional<z.ZodString>;
  filename: z.ZodOptional<z.ZodString>;
  sizeBytes: z.ZodOptional<z.ZodNumber>;
  source: z.ZodOptional<
    z.ZodObject<{
      entityType: z.ZodOptional<z.ZodString>;
      entityId: z.ZodOptional<z.ZodString>;
      attachmentType: z.ZodOptional<z.ZodString>;
    }>
  >;
}> = z.object({
  mediaType: z.string(),
  url: z.string(),
  downloadUrl: z.string().optional(),
  previewUrl: z.string().optional(),
  filename: z.string().optional(),
  sizeBytes: z.number().optional(),
  source: z
    .object({
      entityType: z.string().optional(),
      entityId: z.string().optional(),
      attachmentType: z.string().optional(),
    })
    .optional(),
});

export type CreateResultAttachment = z.infer<
  typeof createResultAttachmentSchema
>;

export type CreateResult =
  | {
      success: true;
      data: {
        entityId?: string;
        jobId?: string;
        status: string;
        attachment?: CreateResultAttachment;
      };
    }
  | { success: false; error: string };

/**
 * Plugin create interceptors can either fully handle creation,
 * or continue with a rewritten normalized input.
 */
export type CreateInterceptionResult =
  | { kind: "handled"; result: CreateResult }
  | { kind: "continue"; input: CreateInput };

export type CreateInterceptor = (
  input: CreateInput,
  executionContext: CreateExecutionContext,
) => Promise<CreateInterceptionResult>;

export interface UploadSaveInput {
  upload: CreateFromUploadInput;
  title?: string;
  visibility?: ContentVisibility;
}

export type UploadSaveHandler = (
  input: UploadSaveInput,
  executionContext: CreateExecutionContext,
) => Promise<CreateResult>;

export interface UploadSaveHandlerRegistration {
  entityType: string;
  mediaTypes: string[];
  handler: UploadSaveHandler;
}

/**
 * Called before an entity is persisted (on create or update). Throws to reject
 * the write with an operator-facing error. Use this for cross-entity invariants
 * the per-entity Zod schema cannot express.
 */
export type PersistValidator<T extends BaseEntity = BaseEntity> = (
  entity: T,
  context: { operation: "create" | "update" },
) => Promise<void>;

/**
 * Minimal event bus contract used by entity-service for lifecycle events.
 * Kept structural to avoid coupling this package to a concrete messaging service.
 */
export interface EntityEventBus {
  send(request: {
    type: string;
    payload: Record<string, unknown>;
    sender: string;
    target?: string;
    metadata?: Record<string, unknown>;
    broadcast?: boolean;
  }): Promise<unknown>;
}

/**
 * Interface for entity adapter - handles conversion between entities and markdown
 * following the hybrid storage model
 *
 * @template TEntity - The full entity type
 * @template TMetadata - The metadata type (defaults to Record<string, unknown>)
 */
export interface EntityAdapter<
  TEntity extends BaseEntity<TMetadata>,
  TMetadata = Record<string, unknown>,
> {
  entityType: string;

  /**
   * One declarative sentence describing what this entity type is. This is the
   * data the model reads to select `entityType`, replacing hardcoded example
   * phrasings in system instructions. Required: every type must define itself.
   */
  purpose: string;

  schema: EntitySchema<TEntity>;

  // Convert entity to markdown content (may include frontmatter for entity-specific fields)
  toMarkdown(entity: TEntity): string;

  // Extract entity-specific fields from markdown
  // Returns Partial<TEntity> as core fields come from database
  fromMarkdown(markdown: string): Partial<TEntity>;

  // Extract metadata from entity for search/filtering - now strongly typed
  extractMetadata(entity: TEntity): TMetadata;

  // Parse frontmatter metadata from markdown
  parseFrontMatter<TFrontmatter>(
    markdown: string,
    schema: { parse(data: unknown): TFrontmatter },
  ): TFrontmatter;

  // Generate frontmatter for markdown
  generateFrontMatter(entity: TEntity): string;

  /** Optional: Zod schema for frontmatter fields. Used by Studio config generation. */
  frontmatterSchema?: z.ZodObject<z.ZodRawShape>;

  /** Optional: Declares this entity type is a singleton (one file, e.g., identity/identity.md). Used by Studio to generate files collection. */
  isSingleton?: boolean;

  /** Optional: Whether this entity has a free-form markdown body below frontmatter. Defaults to true. When false, Studio omits the body widget. */
  hasBody?: boolean;

  /** Returns a markdown body template with section headings for this entity type. Empty string for free-form entities. */
  getBodyTemplate(): string;

  /** Optional: Declares that this entity type supports cover images via coverImageId in frontmatter */
  supportsCoverImage?: boolean;

  /**
   * Optional: the `metadata.status` values that count as published for
   * `publishedOnly` queries (production site builds). Declaring this makes the
   * list exact — entities without a status are NOT published. When absent, the
   * default lifecycle semantics apply: status `published`, `active`, or no
   * status at all.
   */
  publishedStatuses?: string[];

  /** Optional: Extract coverImageId from entity content/frontmatter */
  getCoverImageId?(entity: TEntity): string | undefined;

  /**
   * Optional: build the markdown content and metadata for a queued-generation stub.
   * When undefined, this entity type does not support prompt-based queued creation
   * via system_create; the tool will reject the call rather than silently degrade.
   * The returned metadata must satisfy this entity's metadata schema (with
   * status set to "generating"); central code only stamps id/timestamps/visibility.
   */
  buildStub?(input: { id: string; title: string }): {
    content: string;
    metadata: TMetadata;
  };
}

/**
 * Sort field specification for multi-field sorting
 */
export interface SortField {
  /** Field to sort by - "created", "updated", or a metadata field name */
  field: string;
  /** Sort direction */
  direction: "asc" | "desc";
  /** Sort NULL values before non-NULL values (default: false / SQLite default) */
  nullsFirst?: boolean;
}

/**
 * List entities options
 * Generic over metadata type for type-safe filtering
 */
export interface EntityReadOptions {
  /** Bounds SQL result transfer, suppresses raw diagnostics, and leaves entity
   * image references unexpanded. Not a bound on database or adapter execution.
   */
  readBudget?: EntityReadBudget;
  /** Request-owned embedding capability, e.g. a prepaid guest search. */
  queryEmbedding?: QueryEmbedding;
  /** Cooperative boundary checks, not proof of remote SQL cancellation. */
  signal?: AbortSignal;
}

export interface ListOptions<
  TMetadata = Record<string, unknown>,
> extends EntityReadOptions {
  limit?: number;
  offset?: number;
  /** Multi-field sorting - supports system fields (created, updated) and metadata fields */
  sortFields?: SortField[];
  filter?: {
    // Typed metadata filter - partial match on metadata fields
    metadata?: Partial<TMetadata>;
    /** Literal substring search through serialized content, including frontmatter. */
    contentContains?: string | undefined;
    /** Exact visibility, intersected with visibilityScope rather than widening it. */
    visibility?: ContentVisibility | undefined;
    visibilityScope?: ContentVisibility | undefined;
  };
  /** Filter to only entities with metadata.status = "published" */
  publishedOnly?: boolean;
}

/**
 * Search options
 */
export interface SearchOptions extends EntityReadOptions {
  limit?: number;
  offset?: number;
  types?: string[];
  excludeTypes?: string[];
  sortBy?: "relevance" | "created" | "updated";
  sortDirection?: "asc" | "desc";
  /** Score multipliers per entity type - applied after initial search */
  weight?: Record<string, number>;
  visibilityScope?: ContentVisibility | undefined;
  /** Include queued/failed generation stubs in search results (default: false) */
  includeUngenerated?: boolean;
  /** Minimum relevance score to return. Omit for no score cutoff. */
  minScore?: number;
}

export type ProjectionSourceRole =
  "canonical" | "primary" | "secondary" | "supporting" | "ambient" | "excluded";

/**
 * Configuration for entity type registration
 */
export interface EntityTypeConfig {
  /** Score multiplier for search results (default: 1.0) */
  weight?: number;
  /** Whether to generate embeddings for this entity type (default: true).
   *  Set to false for entity types with non-textual content (e.g., images). */
  embeddable?: boolean;
  /** Whether to index serialized content in full-text search (default: true).
   *  Set to false for binary entity types. Mutations remove stale FTS rows. */
  fullTextSearchable?: boolean;
  /** Durable binary storage policy. Absence means inline/text storage. */
  binaryStorage?: "asset";
  /** Whether this entity type may be used as source material for derived projections (default: true).
   *  Set to false for projection outputs that would create feedback loops. */
  projectionSource?: boolean;
  /** Default source authority role for derived projections. Entity types own
   *  their durable/source character; consumers may map roles to local policy. */
  projectionSourceRole?: ProjectionSourceRole;
  /** Publish semantics for status-bearing entity types. Statuses listed here
   *  represent publication commitment/execution states and require the
   *  `publish` entity action when entered or modified. */
  publish?: {
    publishStatuses: string[];
  };
}

/**
 * Core entity service interface for read-only operations
 * Used by core plugins that need entity access but shouldn't modify entities
 */
export interface GetEntityRequest extends EntityReadOptions {
  entityType: string;
  id: string;
  /**
   * Optional visibility scope. Undefined fails closed to "public" — callers
   * with elevated access must opt up explicitly.
   */
  visibilityScope?: ContentVisibility | undefined;
}

export type GetEntityRawRequest = GetEntityRequest;

export interface ProjectionOwnedEntityRequest {
  entityType: string;
  id: string;
}

export interface ListEntitiesRequest {
  entityType: string;
  options?: ListOptions | undefined;
}

export interface CountEntitiesRequest {
  entityType: string;
  options?: Pick<ListOptions, "publishedOnly" | "filter"> | undefined;
}

export interface CreateEntityRequest<T extends BaseEntity> {
  entity: EntityInput<T>;
  /** Prepared bytes committed in the same transaction as their entity reference. */
  preparedAsset?: PreparedAsset | undefined;
  options?: CreateEntityOptions | undefined;
}

export interface CreateEntityFromMarkdownRequest {
  input: CreateEntityFromMarkdownInput;
  options?: CreateEntityOptions | undefined;
}

export interface UpdateEntityRequest<T extends BaseEntity> {
  entity: T;
  /** Prepared bytes committed in the same transaction as their entity reference. */
  preparedAsset?: PreparedAsset | undefined;
  options?: UpdateEntityOptions | undefined;
}

export interface DeleteEntityRequest {
  entityType: string;
  id: string;
  options?:
    Pick<EntityJobOptions, "eventContext" | "persistenceOrigin"> | undefined;
}

export interface UpsertEntityRequest<T extends BaseEntity> {
  entity: T;
  /** Prepared bytes committed in the same transaction as their entity reference. */
  preparedAsset?: PreparedAsset | undefined;
  options?: EntityJobOptions | undefined;
}

export interface EntitySearchRequest {
  query: string;
  options?: SearchOptions | undefined;
}

export interface SearchWithDistancesRequest {
  query: string;
}

export interface SemanticEntityReference {
  entityId: string;
  entityType: string;
}

export interface ProjectSemanticSpaceRequest {
  /** Entity types to include as projected points. Empty or omitted includes all types. */
  types?: string[];
  /** Optional entity used as the semantic origin. Missing origins fall back to the point centroid. */
  origin?: SemanticEntityReference;
  /** Include pairwise neighbors at or below this cosine distance. */
  maxNeighborDistance?: number;
  /** Undefined fails closed to public-only visibility. */
  visibilityScope?: ContentVisibility | undefined;
}

export interface SemanticSpacePoint extends SemanticEntityReference {
  /** Coordinates on the first two principal components of the semantic space. */
  coordinates: [number, number];
  /** Cosine distance from the requested origin or the fallback centroid. */
  distanceToOrigin: number;
}

export interface SemanticSpaceNeighbor {
  source: SemanticEntityReference;
  target: SemanticEntityReference;
  distance: number;
}

export type SemanticSpaceOrigin =
  ({ kind: "entity" } & SemanticEntityReference) | { kind: "centroid" };

export interface SemanticSpaceDistanceRange {
  min: number;
  max: number;
}

export interface SemanticSpaceProjection {
  origin: SemanticSpaceOrigin;
  points: SemanticSpacePoint[];
  neighbors: SemanticSpaceNeighbor[];
  /** Observed origin-distance extent for projection-independent scaling. */
  distanceRange: SemanticSpaceDistanceRange;
}

/**
 * Context passed to all DataSource operations
 * Contains internal state that should not be mixed with user query parameters
 */
export interface BaseDataSourceContext {
  /**
   * Whether to filter to only published/complete content
   * Set by site-builder: true for production, false for preview
   */
  publishedOnly?: boolean;

  /**
   * Scoped entity service that auto-applies publishedOnly filter
   * Datasources should use this instead of their injected entityService
   * to ensure consistent filtering behavior across environments
   */
  entityService: EntityServiceClient;
}

export type DataSourceSchema<T> = z.ZodType<T, unknown>;

/**
 * DataSource Interface
 *
 * Provides data for templates through fetch, generate, or transform operations.
 * DataSources are registered in the DataSourceRegistry and referenced by templates
 * via their dataSourceId property.
 */
/** Runtime-only, read-only access for caller-bound generation. */
export interface DataSourceGenerationContext {
  readonly visibilityScope: ContentVisibility;
  readonly signal?: AbortSignal;
  getEntity(entityType: string, id: string): Promise<BaseEntity | null>;
  search(
    query: string,
    options?: Pick<SearchOptions, "limit" | "weight">,
  ): Promise<SearchResult[]>;
}

export interface DataSource {
  /**
   * Unique identifier for this data source
   */
  id: string;

  /**
   * Human-readable name for this data source
   */
  name: string;

  /**
   * Optional description of what this data source provides
   */
  description?: string;

  /**
   * Optional: Fetch existing data
   * Used by data sources that aggregate or retrieve data (e.g., dashboard stats, API data)
   * DataSources validate output using the provided schema
   * @param query - Query parameters for fetching data
   * @param outputSchema - Schema for validating output data
   * @param context - Context with environment
   */
  fetch?: <T>(
    query: unknown,
    outputSchema: z.ZodSchema<T>,
    context: BaseDataSourceContext,
  ) => Promise<T>;

  /**
   * Optional: Generate new content
   * Used by data sources that create content (e.g., AI-generated content, reports)
   */
  generate?: <T>(
    request: unknown,
    schema: z.ZodSchema<T>,
    signal?: AbortSignal,
  ) => Promise<T>;

  /**
   * Opt-in scoped generation. All content reads must use this runtime context,
   * not ambient entity services/caches. Never fall back to generate().
   */
  generateScoped?: <T>(
    request: unknown,
    schema: z.ZodSchema<T>,
    context: DataSourceGenerationContext,
  ) => Promise<T>;

  /**
   * Optional: Transform content between formats
   * Used by data sources that convert content (e.g., markdown to HTML, data formatting)
   */
  transform?: <T>(
    content: unknown,
    format: string,
    schema: z.ZodSchema<T>,
  ) => Promise<T>;
}

/**
 * DataSource capabilities for discovery and validation
 */
export interface DataSourceCapabilities {
  canFetch: boolean;
  canGenerate: boolean;
  canTransform: boolean;
}

export interface ICoreEntityService {
  // Read-only operations. Without a schema these return the registered
  // BaseEntity view; pass the entity schema you own to get a parsed,
  // proven T instead of asserting one.
  getEntity(request: GetEntityRequest): Promise<BaseEntity | null>;
  getEntity<T extends BaseEntity>(
    request: GetEntityRequest,
    schema: EntitySchema<T>,
  ): Promise<T | null>;

  /**
   * Get entity without content resolution (raw)
   * Used internally to avoid recursion when resolving image references
   */
  getEntityRaw(request: GetEntityRawRequest): Promise<BaseEntity | null>;
  getEntityRaw<T extends BaseEntity>(
    request: GetEntityRawRequest,
    schema: EntitySchema<T>,
  ): Promise<T | null>;

  listEntities(request: ListEntitiesRequest): Promise<BaseEntity[]>;
  listEntities<T extends BaseEntity>(
    request: ListEntitiesRequest,
    schema: EntitySchema<T>,
  ): Promise<T[]>;

  search(request: EntitySearchRequest): Promise<SearchResult<BaseEntity>[]>;
  search<T extends BaseEntity>(
    request: EntitySearchRequest,
    schema: EntitySchema<T>,
  ): Promise<SearchResult<T>[]>;

  /** Return embedded entities with raw cosine distance to a query. */
  searchWithDistances(
    request: SearchWithDistancesRequest,
  ): Promise<Array<{ entityId: string; entityType: string; distance: number }>>;

  /** Project visible entities into a provider-independent semantic space. */
  projectSemanticSpace(
    request: ProjectSemanticSpaceRequest,
  ): Promise<SemanticSpaceProjection>;

  // Entity type information
  getEntityTypes(): string[];
  hasEntityType(type: string): boolean;

  // Entity counts
  countEntities(request: CountEntitiesRequest): Promise<number>;
  /**
   * Group counts by entity type. Fails closed: undefined visibilityScope
   * filters to public-only counts so aggregate insights cannot reveal
   * non-public entity existence.
   */
  getEntityCounts(
    visibilityScope?: ContentVisibility,
  ): Promise<Array<{ entityType: string; count: number }>>;

  /** Get configuration for a specific entity type */
  getEntityTypeConfig(type: string): EntityTypeConfig;

  /** Get weight map for all registered entity types with non-default weights */
  getWeightMap(): Record<string, number>;
}

/**
 * Entity service interface for managing brain entities
 */
export interface IEntitiesNamespace {
  /** Register a new entity type with schema and adapter */
  register<TEntity extends BaseEntity>(
    entityType: string,
    schema: EntitySchema<TEntity>,
    adapter: EntityAdapter<TEntity>,
    config?: EntityTypeConfig,
  ): void;

  /**
   * Get the adapter for an entity type.
   *
   * Returns the structural `EntityAdapter<BaseEntity>` view — namespace
   * consumers don't narrow by entity type. For typed access tied to a
   * specific `TEntity`, use the underlying `EntityRegistry.getAdapter<T>`
   * directly (see `entity-serializer.ts`).
   */
  getAdapter(entityType: string): EntityAdapter<BaseEntity> | undefined;

  /** Extend an adapter's frontmatterSchema with additional fields */
  extendFrontmatterSchema(
    type: string,
    extension: z.ZodObject<z.ZodRawShape>,
  ): void;

  /** Get effective frontmatter schema (base + extensions) for an entity type */
  getEffectiveFrontmatterSchema(
    type: string,
  ): z.ZodObject<z.ZodRawShape> | undefined;

  /** Update an existing entity */
  update<TEntity extends BaseEntity>(
    entity: TEntity,
  ): Promise<{ entityId: string; jobId: string }>;

  /** Register a data source for dynamic content */
  registerDataSource(dataSource: DataSource): void;

  /** Register plugin-owned validation that runs before create/update persistence. */
  registerPersistValidator(
    entityType: string,
    validator: PersistValidator,
  ): void;

  /** Register a create interceptor for this plugin's entity type */
  registerCreateInterceptor(
    entityType: string,
    interceptor: CreateInterceptor,
  ): void;

  /** Register a raw-upload durable save handler for this plugin's entity type */
  registerUploadSaveHandler(registration: UploadSaveHandlerRegistration): void;

  /** Look up the registered upload-save handler claiming a media type */
  getUploadSaveHandler(
    mediaType: string,
  ): UploadSaveHandlerRegistration | undefined;
}

export interface EmbeddingBackfillResult {
  queued: number;
  skipped: number;
}

export interface EmbeddingFailureReference {
  entityId: string;
  entityType: string;
  contentHash: string;
}

export interface EmbeddingIndexStats {
  missingEmbeddings: number;
  staleEmbeddings: number;
  failedEmbeddings: number;
  /** Entities whose type generates embeddings (non-embeddable types excluded). */
  embeddableEntities: number;
  /** Embeddable entities whose embedding is present and current. */
  embeddedEntities: number;
}

export interface IndexReadinessOptions {
  /** Stop polling after this duration. Omit for an owning runtime monitor. */
  timeoutMs?: number;
  intervalMs?: number;
  /** Cancels readiness polling when the owning runtime shuts down. */
  signal?: AbortSignal;
}

export interface IndexReadinessStatus extends EmbeddingIndexStats {
  ready: boolean;
  degraded: boolean;
  activeEmbeddingJobs: number;
}

export interface BulkMutationInput {
  source: string;
  operationId: string;
}

export interface DurableBulkMutationRootInput extends BulkMutationInput {
  rootJobId: string;
  expectedChildren: number;
}

export interface DurableBulkMutationChildInput extends DurableBulkMutationRootInput {
  childKey: string;
  jobId: string;
}

export interface ProjectionBatchOwnedJob {
  jobId: string;
  childKey: string;
  status: "pending" | "processing" | "completed" | "failed";
  terminalAt: number | null;
}

export type ProjectionBatchRootReader = (
  rootJobId: string,
  operationId: string,
) => Promise<readonly ProjectionBatchOwnedJob[]>;

export interface ProjectionBatchRecoveryResult {
  fencedCallbacks: number;
  releasedDurableRoots: number;
}

export interface SettleDurableBulkMutationChildInput {
  operationId: string;
  childKey: string;
  jobId: string;
  outcome: "completed" | "failed";
}

/**
 * The entity-service surface ordinary plugins receive. A real interface
 * rather than Omit<EntityService, ...>: mapped types collapse overloaded
 * methods (like the schema-taking reads) down to one signature.
 */
export interface EntityServiceClient extends ICoreEntityService {
  /** Internal source-authority check used by persistence integrations. */
  isProjectionOwnedEntity(
    request: ProjectionOwnedEntityRequest,
  ): Promise<boolean>;

  // Durable entity-to-directory export coordination
  listPendingEntityExports(): Promise<EntityExportIntent[]>;
  hasPendingEntityExports(): Promise<boolean>;
  acknowledgeEntityExports(
    request: AcknowledgeEntityExportsRequest,
  ): Promise<number>;

  // Callback-scoped bulk mutation coordination
  runBulkMutation<TResult>(
    input: BulkMutationInput,
    mutation: () => Promise<TResult>,
  ): Promise<TResult>;

  // Mutations
  createEntity<T extends BaseEntity>(
    request: CreateEntityRequest<T>,
  ): Promise<EntityMutationResult>;
  createEntityFromMarkdown(
    request: CreateEntityFromMarkdownRequest,
  ): Promise<EntityMutationResult>;
  updateEntity<T extends BaseEntity>(
    request: UpdateEntityRequest<T>,
  ): Promise<EntityMutationResult>;
  deleteEntity(request: DeleteEntityRequest): Promise<boolean>;
  upsertEntity<T extends BaseEntity>(
    request: UpsertEntityRequest<T>,
  ): Promise<EntityMutationResult & { created: boolean }>;
  storeEmbedding(data: StoreEmbeddingData): Promise<void>;
  reconcileProjectionTargets(
    targets: readonly ProjectionChangedTarget[],
  ): Promise<void>;
  backfillMissingEmbeddings(): Promise<EmbeddingBackfillResult>;
  isIndexReady(): boolean;
  awaitIndexReady(
    options: IndexReadinessOptions,
  ): Promise<IndexReadinessStatus>;

  // Serialization
  serializeEntity(entity: BaseEntity): string;
  deserializeEntity(markdown: string, entityType: string): Partial<BaseEntity>;

  // Counts
  countEmbeddings(): Promise<number>;

  // Lifecycle
  initialize(): Promise<void>;

  // Job status
  getAsyncJobStatus(jobId: string): Promise<{
    status: "pending" | "processing" | "completed" | "failed";
    error?: string;
  } | null>;
}

/**
 * The durable bulk-mutation coordination surface, kept out of
 * EntityServiceClient so ordinary plugins cannot reach it by accident.
 *
 * Projection-owning plugins genuinely need it. Publishing it as its own
 * capability makes that dependency explicit and checkable — the alternative
 * was asserting the narrowed client back up to the full EntityService, which
 * also silently re-granted everything else the client omits.
 */
export type DurableBulkMutationCoordinator = Pick<
  EntityService,
  | "prepareDurableBulkMutation"
  | "finalizeDurableBulkMutationEnqueue"
  | "failDurableBulkMutationEnqueue"
  | "runDurableBulkMutationChild"
  | "settleDurableBulkMutationChild"
>;

export interface EntityService extends EntityServiceClient {
  /** Visibility-scoped entity and the revision derived from its stored row. */
  getEntityWriteSnapshot(
    request: GetEntityRequest,
  ): Promise<EntityWriteSnapshot | null>;
  // Scheduler-owned projection coordination
  getProjectionStore(): ProjectionStore;
  setProjectionWakeup(wakeup: () => Promise<void>): () => void;

  // Durable bulk mutation coordination
  prepareDurableBulkMutation(
    input: DurableBulkMutationRootInput,
  ): Promise<void>;
  finalizeDurableBulkMutationEnqueue(operationId: string): Promise<void>;
  failDurableBulkMutationEnqueue(operationId: string): Promise<void>;
  runDurableBulkMutationChild<TResult>(
    input: DurableBulkMutationChildInput,
    mutation: () => Promise<TResult>,
  ): Promise<TResult>;
  settleDurableBulkMutationChild(
    input: SettleDurableBulkMutationChildInput,
  ): Promise<boolean>;
  recoverProjectionBatches(
    readRoot: ProjectionBatchRootReader,
  ): Promise<ProjectionBatchRecoveryResult>;
}

/**
 * Entity Registry interface for managing entity types and their schemas/adapters
 */
export interface EntityRegistry {
  registerEntityType<
    TEntity extends BaseEntity<TMetadata>,
    TMetadata extends Record<string, unknown> = Record<string, unknown>,
  >(
    type: string,
    schema: UnknownEntitySchema,
    adapter: EntityAdapter<TEntity, TMetadata>,
    config?: EntityTypeConfig,
  ): void;

  /** Remove an entity type after failed plugin registration or shell teardown. */
  unregisterEntityType(type: string): void;

  getSchema(type: string): UnknownEntitySchema;

  getAdapter(type: string): EntityAdapter<BaseEntity>;

  hasEntityType(type: string): boolean;

  validateEntity(type: string, entity: unknown): BaseEntity;

  getAllEntityTypes(): string[];

  /** Get configuration for a specific entity type */
  getEntityTypeConfig(type: string): EntityTypeConfig;

  /** Get weight map for all registered entity types with non-default weights */
  getWeightMap(): Record<string, number>;

  registerCreateInterceptor(type: string, interceptor: CreateInterceptor): void;

  getCreateInterceptor(type: string): CreateInterceptor | undefined;

  registerUploadSaveHandler(registration: UploadSaveHandlerRegistration): void;

  getUploadSaveHandler(
    mediaType: string,
  ): UploadSaveHandlerRegistration | undefined;

  registerPersistValidator(type: string, validator: PersistValidator): void;

  getPersistValidator(type: string): PersistValidator | undefined;

  /**
   * Extend an adapter's frontmatterSchema with additional fields.
   * Used by plugins to add domain-specific fields (e.g., professional-site adds tagline to profile).
   * Extensions are merged into the effective schema returned by getEffectiveFrontmatterSchema().
   */
  extendFrontmatterSchema(
    type: string,
    extension: z.ZodObject<z.ZodRawShape>,
  ): void;

  /**
   * Get the effective frontmatter schema for an entity type,
   * with all registered extensions merged in.
   * Returns undefined if the adapter has no frontmatterSchema.
   */
  getEffectiveFrontmatterSchema(
    type: string,
  ): z.ZodObject<z.ZodRawShape> | undefined;
}

/**
 * Database configuration for entity service
 */
export type { DbConfig as EntityDbConfig } from "@brains/contracts";

/**
 * The read-only projection of the entity service.
 *
 * What a plugin is handed when it has no business writing: insight handlers
 * compute over entities and return a summary. A projection of
 * `ICoreEntityService` rather than a hand-written parallel interface, so a
 * method that changes shape fails to compile here instead of drifting.
 *
 * `@brains/plugins` restates this as `IEntityService` on its published
 * authoring surface; `public-surface-soundness.test.ts` holds the two
 * together.
 */
export type ReadOnlyEntityService = Pick<
  ICoreEntityService,
  | "getEntity"
  | "listEntities"
  | "search"
  | "searchWithDistances"
  | "getEntityTypes"
  | "hasEntityType"
  | "countEntities"
  | "getEntityCounts"
  | "getEntityTypeConfig"
>;
